import { DataObjectValidFormDataSchema } from './DataObjectValidFormDataSchema';
import {
  formatIsoDate,
  transformToValidFromAndValidTo,
} from '../../common/utils';
import { z, ZodObject, ZodRawShape } from 'zod/v3';
import { DimensionValidFormDataType } from '../types';
import {
  ExecutionCalendarFormDataType,
  ExecutionPeriodFormDataType,
  ExecutionPublicationFormDataType,
  ValidationRuleValidDataType,
} from './types';

const sortBySequenceNo = <T extends { sequenceNumber?: number | null }>(
  toSort: T[],
): T[] =>
  toSort.toSorted(
    (a, b) =>
      (a.sequenceNumber ?? Number.MAX_SAFE_INTEGER) -
      (b.sequenceNumber ?? Number.MAX_SAFE_INTEGER),
  );

const transform = <T extends ZodRawShape>(schemaVariant: ZodObject<T>) =>
  transformToValidFromAndValidTo(schemaVariant).transform(
    ({
      dimensions,
      indicators,
      groupAndSubgroupId,
      execution,
      executionDeadlines,
      validationRules,
      ...rest
    }) => {
      const groupAndSubgroupIds = groupAndSubgroupId?.split('-') || [];
      return {
        ...rest,
        groupId: groupAndSubgroupIds[0],
        subgroupId: groupAndSubgroupIds[1],
        dimensions: sortBySequenceNo(dimensions ?? []).map(
          ({
            classifier,
            dimensionType,
            ...dimension
          }: DimensionValidFormDataType) => ({
            ...dimension,
            classifierId: classifier?.classifier.id,
            categoryId: classifier?.category?.id,
            allCategories: classifier?.allCategories,
            levelNumber: classifier?.levelNumber,
            dimensionTypeId: dimensionType?.id,
          }),
        ),
        indicators: sortBySequenceNo(indicators ?? []),
        execution: {
          ...execution,
          executionCalendar: execution.executionCalendar.map(
            ({ validPeriod, ...calendar }: ExecutionCalendarFormDataType) => ({
              ...calendar,
              validFrom: formatIsoDate(validPeriod[0]),
              validTo: formatIsoDate(validPeriod[1]),
            }),
          ),
          executionPeriods: execution.executionPeriods.map(
            ({ validPeriod, ...period }: ExecutionPeriodFormDataType) => ({
              ...period,
              validFrom: formatIsoDate(validPeriod[0]),
              validTo: formatIsoDate(validPeriod[1]),
            }),
          ),
          executionPublications: execution.executionPublications.map(
            ({
              validPeriod,
              ...publication
            }: ExecutionPublicationFormDataType) => ({
              ...publication,
              validFrom: formatIsoDate(validPeriod[0]),
              validTo: formatIsoDate(validPeriod[1]),
            }),
          ),
          executionDeadlines,
        },
        validationRules: validationRules.map(
          ({ validPeriod, ...rule }: ValidationRuleValidDataType) => ({
            ...rule,
            validFrom: formatIsoDate(validPeriod[0]),
            validTo: formatIsoDate(validPeriod[1]),
          }),
        ),
      };
    },
  );

export const DataObjectMutationDtoSchema = z.union([
  transform(DataObjectValidFormDataSchema.options[0]),
  transform(DataObjectValidFormDataSchema.options[1]),
  transform(DataObjectValidFormDataSchema.options[2]),
  transform(DataObjectValidFormDataSchema.options[3]),
]);
